import { defineStore } from 'pinia';
import { cloneDeep, isEqual } from 'lodash';
import * as logger from '../utils/logger';
import { getJobsById, getRelatedJobInfo } from '../services/jobsearch';
import { VisitedJobsHistory } from '../utils/client-side-storage/visitedJobsHistory';
import { prepareCustomFields } from '../utils/custom-fields';

export const useJobDataStore = defineStore('job', {
    state: () => ({
        title: undefined,
        locations: undefined,
        jobFields: undefined,
        isChiffre: undefined,
        isSeamless: undefined,
        link: undefined,
        date: undefined,
        company: {
            id: undefined,
            name: undefined,
            slug: undefined,
            logo: {
                companysquare: undefined,
            },
            locations: undefined,
            branding: {
                isBrandable: false,
                color: '',
                headerImage: null,
            },
        },
        relatedJobs: [],
        recentJobs: [],
        jobId: undefined,
        productType: null,
        keyFactBox: null,
        customFieldsObject: { fields: undefined, previousFields: undefined },
    }),
    getters: {
        /** @returns {CustomField[]} */
        customFields: state => state.customFieldsObject.fields,
        shouldFetchCustomFields: state => !state.isChiffre && state.customFieldsObject.fields === undefined,
        hasCustomFields: state => Boolean(state.customFieldsObject.fields?.length > 0),
        jobDataFetched: state => state.locations !== undefined,
        relatedJobsFetched: state => state.relatedJobs.length !== 0,
        recentJobsFetched: state => state.recentJobs.length !== 0,
        companyFetched: state => state.company.id !== undefined,
        jobPageUrl: (state) => {
            const url = new URL('jobs', window.location.origin);
            if (state.locations) {
                url.searchParams.set('locations', state.locations);
            }
            if (state.jobFields?.length) {
                state.jobFields.forEach(jobField => url.searchParams.append('jobFields[]', jobField));
            }
            return url.href;
        },
        companyName: state => (state.isChiffre ? 'Arbeitgeber verdeckt' : state.company.name),
        serializedCustomFields: state => state.customFieldsObject
            .fields?.filter(({ value }) => {
                if (Array.isArray(value)) return value.length;
                if (value || value === 0) return true;
                return false;
            })
            .map(({ id, value, type }) => ({ id, value, type })) ?? [],
    },
    actions: {
        async fetchRelatedJobs(jobId) {
            try {
                const { data } = await getRelatedJobInfo(jobId);
                this.$patch({
                    relatedJobs: data,
                });
            } catch (exception) {
                logger.error({
                    exception,
                    fingerprint: ['Fetching related job data failed.'],
                    message: 'Fetching related job data failed.',
                    extra: {
                        jobId,
                        response: exception?.response?.data ?
                            JSON.stringify(exception.response.data) :
                            'No response available.',
                    },
                });
            }
        },
        async fetchRecentJobs() {
            const recentJobIds = await VisitedJobsHistory.getJobIds();
            try {
                const { data } = await getJobsById(recentJobIds.slice(0, 3));
                this.$patch({
                    recentJobs: data,
                });
            } catch (exception) {
                logger.error({
                    exception,
                    fingerprint: ['Fetching data for recently viewed jobs failed.'],
                    message: 'Fetching data for recently viewed jobs failed.',
                    extra: {
                        recentJobIds,
                        response: exception?.response?.data ?
                            JSON.stringify(exception.response.data) :
                            'No response available.',
                    },
                });
            }
        },
        // Use new fields while preserving user-input from fields that have not changed
        patchCustomFields(fields) {
            this.$patch((state) => {
                // Prepare data structures for easy lookup of fields + their values
                const previousFieldsMap = new Map(state.customFieldsObject.previousFields
                    .map(field => [field.id, field]));
                const newFieldsMap = new Map(cloneDeep(fields)
                    .map(field => [field.id, field]));
                const filledOutFieldValuesMap = new Map(this.serializedCustomFields
                    .map(({ id, value }) => [id, value]));

                // Remove all old fields without breaking reactivity
                state.customFieldsObject.fields.length = 0;

                // Iterate over new fields only, implicitly removing old fields that no longer exist
                newFieldsMap.forEach((newField, newFieldId) => {
                    if (previousFieldsMap.has(newFieldId)) {
                        const previousField = previousFieldsMap.get(newFieldId);
                        // If the field has not changed and is filled out, preserve its value
                        if (isEqual(newField, previousField) && filledOutFieldValuesMap.has(newFieldId)) {
                            // eslint-disable-next-line no-param-reassign
                            newField.value = filledOutFieldValuesMap.get(newFieldId);
                        }
                    }
                    // Push the new field into the existing reactive array
                    state.customFieldsObject.fields.push(newField);
                });

                state.customFieldsObject.previousFields = fields;
            });
        },
        setCustomFields(customFieldData) {
            try {
                const fields = prepareCustomFields(customFieldData);

                // No previous response - just store both
                if (!this.customFieldsObject.previousFields) {
                    this.$patch({
                        customFieldsObject: {
                            fields: cloneDeep(fields),
                            previousFields: cloneDeep(fields),
                        },
                    });
                    return;
                }

                // If response is equal to previous response, no need to do anything
                if (isEqual(fields, this.customFieldsObject.previousFields)) {
                    return;
                }

                // If none of the above are true, something changed in the custom fields and we patch them
                this.patchCustomFields(fields);

                logger.info({
                    message: 'Custom fields changed while applicant was on application form.',
                    extra: {
                        jobId: this.jobId,
                    },
                });
            } catch (exception) {
                const fingerprint = this.hasCustomFields ?
                    ['Updating custom fields failed - user might be trapped in application form!'] :
                    ['Fetching custom fields failed.'];

                logger.error({
                    exception,
                    fingerprint,
                    extra: {
                        jobId: this.jobId,
                        exception,
                        response: exception?.response?.data ?
                            JSON.stringify(exception.response.data) :
                            'No response available.',
                    },
                });
            }
        },
    },
    persist: {
        storage: sessionStorage,
    },
});
